import {Grid, Typography} from '@material-ui/core';
import {DialogType} from 'commons/Dialog/CustomDialog';
import DownloadIcon from 'commons/Icon/DownloadIcon';
import {localize} from 'commons/LocalizedText/LocalizedText';
import {CustomTooltip} from 'commons/Tooltip/CustomTooltip';
import {useDialog} from 'hook/DialogProvider';
import {FileService} from 'services/FileService';
import {ErrorName} from 'utils/constants/error.name';
import {generateRow} from 'utils/helpers/RowHelper';
import {timestampToDate} from 'utils/processing/dates';
import CustomIcon from "commons/Icon/CustomIcon";
import {faExclamationTriangle} from "@fortawesome/pro-solid-svg-icons";
import {productTestToPrioritize} from "components/ProductTest/ProductTestProjection";
import {makeStyles} from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  toPrioritize: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(1)
  }
}));

export const EccFileDownload = ({file}) => {
  const showDialog = useDialog();

  const downloadFile = () => FileService.downloadFile(file.idCrypted, file.type)
    .catch(errorMessage => {
      if (errorMessage !== ErrorName.ABORT_ERROR) {
        showDialog({
          variant: DialogType.ERROR,
          title: localize('error'),
          description: errorMessage
        });
      }
    });

  return (
    file &&
    <Grid item>
      <CustomTooltip title={`${localize('download')} ${file.name ?? ''}`}>
        <span style={{alignSelf:'center'}}>
          <DownloadIcon onClick={event => {
            event.stopPropagation();
            downloadFile();
          }} />
        </span>
      </CustomTooltip>
    </Grid>
  );
};

const ProductTestNumberTemplate = ({ customerStatusId, productTestNumber, productTestTypeId}) => {
  const classes = useStyles();
  return (
    <Grid alignItems="center" container direction="row" justifyContent="center">
      {productTestToPrioritize(productTestTypeId, customerStatusId) &&
        <CustomIcon
          className={classes.toPrioritize}
          icon={faExclamationTriangle}
          tooltip={localize('toPrioritize')}
        />
      }
      <Typography variant="body2">{productTestNumber}</Typography>
    </Grid>
  );
};

export const testRows = [
  generateRow({
    name: 'companyName',
    label: 'company',
    width: '150px'
  }),
  generateRow({
    name: 'productTestNumber',
    width: '175px',
    template: (row) => (
      <ProductTestNumberTemplate
        customerStatusId={row.customerStatusId}
        productTestNumber={row.productTestNumber}
        productTestTypeId={row.productTestTypeId}
      />
    )
  }),
  generateRow({
    name: 'programName',
    label: 'program',
    width: '150px'
  }),
  generateRow({
    name: 'productType',
    width: '150px'
  }),
  generateRow({
    name: 'productName',
    width: '150px'
  }),
  generateRow({
    name: 'orderForms',
    label: 'labOrderForms',
    width: '125px',
    isSortable: false,
    template: (row) => (
      <Grid alignItems="center" container direction="column" justifyContent="center">
        {row.orderFormProjections.map((r, index) => (
          <EccFileDownload
            file={r.eccFileProjection}
            key={`${r.name}${index}`}
          />
        ))}
      </Grid>
    )
  }),
  generateRow({
    name: 'deliveryDeadline',
    width: '150px',
    template: (row) => (
      <Typography variant="body2">{timestampToDate(row.deadline)}</Typography>
    )
  }),
  generateRow({
    name: 'deliveryDate',
    width: '150px',
    template: (row) => (
      <Typography variant="body2">{timestampToDate(row.deliveryDate)}</Typography>
    )
  }),
  generateRow({
    name: 'scheduledTestDate',
    width: '150px',
    template: (row) => (
      <Typography variant="body2">{timestampToDate(row.scheduledTestDate)}</Typography>
    )
  }),
  generateRow({
    name: 'testDate',
    width: '150px',
    template: (row) => (
      <Typography variant="body2">{timestampToDate(row.testDate)}</Typography>
    )
  }),
  generateRow({
    name: 'laboResultFiles',
    label: 'labReports',
    width: '100px',
    isSortable: false,
    template: (row) => (
      <Grid alignItems="center" container direction="column" justifyContent="center">
        {row.laboResultFiles.map((r, index) => (
          <EccFileDownload
            file={r}
            key={`${r.name}${index}`}
          />
        ))}
      </Grid>
    )
  }),
  generateRow({
    name: 'bills',
    width: '100px',
    isSortable: false,
    template: (row) => (
      <Grid alignItems="center" container direction="column" justifyContent="center">
        {row.billProjections.map((r, index) => (
          <EccFileDownload
            file={r.eccFileProjection}
            key={`${r.name}${index}`}
          />
        ))}
      </Grid>
    )
  }),
  generateRow({
    name: 'partLab',
    width: '150px',
    isSortable: false,
    template: (row) => (
      <Typography variant="body2">{row.partLab ? localize('yes') : localize('no')}</Typography>
    )
  })
];

export const addNewRows = (rows, newRows) => {
  return [
    ...rows,
    ...newRows.map(nR => {
      if(!rows.find(r => r.id === nR.id)){
        return nR;
      }else{
        return null;
      }
    }).filter(nR => nR !== null)
  ]
}